import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { TRACKING, hasGoogleAds, hasMetaPixel, hasLinkedIn, hasGTM, trackPageView, trackPhoneClick, trackEmailClick, trackConversion } from "@/lib/pixels";

/**
 * Loads all configured tracking pixels via <Helmet>.
 * Skips any platform whose env var is empty — production safe, no console errors.
 * Also fires SPA page_view events on every route change AND auto-tracks
 * phone (tel:) and email (mailto:) clicks site-wide via a single delegated listener.
 */
export default function PixelScripts() {
  const { pathname } = useLocation();

  // SPA page-view tracking — fire after each route change
  useEffect(() => {
    // Wait one paint so the pixels are present before firing
    const t = setTimeout(() => trackPageView({ path: pathname }), 50);
    return () => clearTimeout(t);
  }, [pathname]);

  // Delegated click tracking for tel: + mailto: links — covers every component
  // without each having to import a hook. Lightweight (one listener total).
  //
  // Also auto-fires the micro-conversion declared on any element via
  // `data-conversion="event_name"` (button or link). Allows site-wide
  // hero-CTA / book / quote intent tracking with zero per-component changes.
  // Auto-fires `book_appointment_click` on any anchor pointing to
  // /book-an-appointment, so every Book button across the site is tracked.
  useEffect(() => {
    // Two independent dedupe trackers — one per branch — so a single element
    // that triggers BOTH branches (e.g. a /book-an-appointment link that ALSO
    // carries data-conversion="hero_cta_click") still fires BOTH events.
    const firedBook = new WeakSet();
    const firedConversion = new WeakSet();
    const onClick = (e) => {
      const target = e.target;
      if (!target || !target.closest) return;

      // 1) Phone + email auto-tracking (existing behaviour)
      const a = target.closest("a[href]");
      if (a) {
        const href = a.getAttribute("href") || "";
        if (href.startsWith("tel:")) {
          trackPhoneClick({ source_page: window.location.pathname, phone: href.replace("tel:", "") });
        } else if (href.startsWith("mailto:")) {
          trackEmailClick({ source_page: window.location.pathname, email: href.replace("mailto:", "").split("?")[0] });
        }
      }

      // 2) Auto-track Book Appointment intent — any link going to /book-an-appointment
      if (a) {
        const href = a.getAttribute("href") || "";
        if (href === "/book-an-appointment" || href.startsWith("/book-an-appointment?")) {
          if (!firedBook.has(a)) {
            trackConversion("book_appointment_click", { source_page: window.location.pathname });
            firedBook.add(a);
            setTimeout(() => firedBook.delete(a), 1500);
          }
        }
      }

      // 3) Declared micro-conversions via data-conversion="event_name"
      const dc = target.closest("[data-conversion]");
      if (dc) {
        if (firedConversion.has(dc)) return;
        const evt = dc.getAttribute("data-conversion");
        if (evt) {
          trackConversion(evt, { source_page: window.location.pathname });
          firedConversion.add(dc);
          setTimeout(() => firedConversion.delete(dc), 1500);
        }
      }
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return (
    <>
      <Helmet>
        {/* ───────────── Google Ads — uses the shared gtag.js loader from index.html.
             GA4 is loaded directly in /public/index.html. Here we only attach an extra
             gtag('config', ...) call for Google Ads when configured. ───────────── */}
        {hasGoogleAds() && (
          <script>{`
            if (typeof window.gtag === 'function') {
              window.gtag('config', '${TRACKING.googleAdsId}');
            }
          `}</script>
        )}

        {/* ───────────── Meta Pixel ───────────── */}
        {hasMetaPixel() && (
          <script>{`
            !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
            n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
            document,'script','https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${TRACKING.metaPixelId}');
            fbq('track', 'PageView');
          `}</script>
        )}
        {hasMetaPixel() && (
          <noscript>{`<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${TRACKING.metaPixelId}&ev=PageView&noscript=1" />`}</noscript>
        )}

        {/* ───────────── LinkedIn Insight Tag ───────────── */}
        {hasLinkedIn() && (
          <script>{`
            _linkedin_partner_id = "${TRACKING.linkedInPartnerId}";
            window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
            window._linkedin_data_partner_ids.push(_linkedin_partner_id);
            (function(l) {
              if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
              window.lintrk.q=[]}
              var s = document.getElementsByTagName("script")[0];
              var b = document.createElement("script");
              b.type = "text/javascript";b.async = true;
              b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
              s.parentNode.insertBefore(b, s);
            })(window.lintrk);
          `}</script>
        )}
        {hasLinkedIn() && (
          <noscript>{`<img height="1" width="1" style="display:none" alt="" src="https://px.ads.linkedin.com/collect/?pid=${TRACKING.linkedInPartnerId}&fmt=gif" />`}</noscript>
        )}

        {/* ───────────── Google Tag Manager (optional — overrides nothing) ───────────── */}
        {hasGTM() && (
          <script>{`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${TRACKING.gtmContainerId}');
          `}</script>
        )}
      </Helmet>
      {/* GTM noscript fallback (needs to be in <body>, but Helmet only supports <head>;
          if user enables GTM later they can also add the iframe via Emergent Support) */}
    </>
  );
}
